import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslations } from "@/hooks/useTranslations";
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  Users, 
  Store, 
  Package, 
  TrendingUp, 
  MapPin, 
  UserCheck, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  Check, 
  CheckCircle,
  X, 
  Eye,
  Calendar,
  FileText,
  Briefcase,
  GraduationCap,
  Clock,
  Star,
  Boxes,
  Palette,
  Shield,
  UserPlus,
  UserCog,
  Crown,
  Activity,
  DollarSign,
  ShoppingCart,
  BarChart3,
  Zap,
  Building2,
  Truck,
  Phone,
  Mail,
  Globe,
  Languages,
  AlertCircle,
  UserCheck as UserCheckIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  MessageSquare,
  TrendingUp as TrendingUpIcon,
  Radio,
  Percent,
  Tag,
  Key
} from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';
import { getCurrencySymbol, formatPriceWithCurrency } from '@/utils/currency';
import { useToast } from '@/hooks/use-toast';
import { usePersistentTabState } from '@/hooks/usePersistentTabState';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { CMSManager } from '@/components/admin/CMSManager';
import { ConfigurationManager } from '@/components/admin/ConfigurationManager';
import { StockManagement } from '@/components/admin/StockManagement';
import { StockTestModeManager } from '@/components/admin/StockTestModeManager';
import { VendorsManager } from '@/components/admin/VendorsManager';
import { UsersManager } from '@/components/admin/UsersManager';
import { DocumentsManagement } from '@/components/hr/DocumentsManagement';
import { EmployeeProfile } from '@/components/hr/EmployeeProfile';
import { LeaveManagement } from '@/components/hr/LeaveManagement';
import { EvaluationManagement } from '@/components/hr/EvaluationManagement';
import { TrainingManagement } from '@/components/hr/TrainingManagement';

import { OrderActionsDialog } from '@/components/admin/OrderActionsDialog';
import { AdminDisputesManager } from '@/components/admin/DisputesManager';
import { AdvertisementsManager } from '@/components/admin/AdvertisementsManager';
import { DealsManager } from '@/components/admin/DealsManager';
import { AdminWithdrawalManager } from '@/components/admin/AdminWithdrawalManager';
import { EnhancedDealsManagerV2 } from '@/components/admin/EnhancedDealsManagerV2';
import { AdminOrderFilters } from '@/components/admin/AdminOrderFilters';
import { TestEmailButton } from '@/components/admin/TestEmailButton';
import { ClearCacheButton } from '@/components/admin/ClearCacheButton';
import { EmployeesManager } from '@/components/admin/EmployeesManager';
import { NewsletterManager } from '@/components/admin/NewsletterManager';
import { PromoCodesManager } from '@/components/admin/PromoCodesManager';
import { GlobalSearch } from '@/components/admin/GlobalSearch';
import { SystemBannersManager } from '@/components/admin/SystemBannersManager';
import { ChatRequestsManager } from '@/components/admin/ChatRequestsManager';
import { AdminMessagingCenter } from '@/components/admin/AdminMessagingCenter';
import { MonitoringPanel } from '@/components/admin/MonitoringPanel';

import { APIKeysManager } from '@/components/admin/APIKeysManager';
import { useAdminPresence } from '@/hooks/useAdminPresence';


interface Profile {
  id: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  role: 'client' | 'vendor' | 'relay' | 'admin' | 'hr' | 'super_admin';
  user_role: 'admin' | 'moderator' | 'hr' | 'manager' | 'relay_operator' | 'employee' | null;
  vendor_status: 'pending' | 'approved' | 'rejected' | null;
  created_at: string;
  updated_at: string;
}

interface VendorApplication {
  id: string;
  user_id: string;
  business_name: string;
  business_description: string | null;
  business_address: string;
  business_phone: string;
  business_email: string;
  business_type: string;
  experience: string | null;
  motivation: string | null;
  status: string;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  profiles?: any;
}

interface Employee {
  id: string;
  user_id: string | null;
  employee_code: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  position: string;
  department: string | null;
  hire_date: string;
  status: string;
  salary_amount: number | null;
  salary_currency: string | null;
  manager_id: string | null;
  relay_id: string | null;
  birth_date: string | null;
  address: string | null;
  created_at: string;
  updated_at: string;
}

interface Relay {
  id: string;
  name: string;
  address: string;
  contact_name: string;
  contact_phone: string;
  latitude: number | null;
  longitude: number | null;
  hours_json: any;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface Order {
  id: string;
  order_number: string;
  client_id: string;
  vendor_id: string;
  product_id: string;
  relay_id: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
  available_at: string | null;
  picked_up_at: string | null;
  expires_at: string | null;
  shelf_location: string | null;
  product?: { title: string };
  client?: { full_name: string; email?: string };
  vendor?: { full_name: string; email?: string; business_email?: string };
  relay?: { name: string };
  pickup_tokens?: Array<{
    qr_svg: string;
    otp_expires_at: string;
  }>;
}

interface Analytics {
  totalUsers: number;
  totalVendors: number;
  totalOrders: number;
  totalRevenue: number;
  revenueCurrency: string;
  totalRelays: number;
  totalEmployees: number;
  activeUsers: number;
  processedOrders: number;
  pendingOrders: number;
  disputes: number;
  visitors: number;
  satisfiedCustomers: number;
}

interface VisitorDetail {
  visitor_id: string;
  ip_address: string | null;
  country: string | null;
  city: string | null;
  page_url: string;
  device_type: string | null;
  browser: string | null;
  created_at: string;
}

const AdminDashboard = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { t: tDb } = useTranslations();
  const { displayCurrency, convertPrice } = useCurrency();
  
  // Activer le heartbeat de présence admin pour tous les admins sur ce dashboard
  useAdminPresence();

  const isAdminOrSuperAdmin = profile?.system_role === 'admin' || profile?.system_role === 'super_admin';

  // State for data
  const [analytics, setAnalytics] = useState<Analytics>({
    totalUsers: 0,
    totalVendors: 0,
    totalOrders: 0,
    totalRevenue: 0,
    revenueCurrency: 'USD',
    totalRelays: 0,
    totalEmployees: 0,
    activeUsers: 0,
    processedOrders: 0,
    pendingOrders: 0,
    disputes: 0,
    visitors: 0,
    satisfiedCustomers: 0,
  });
  const [allUsers, setAllUsers] = useState<Profile[]>([]);
  const [vendorApplications, setVendorApplications] = useState<VendorApplication[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [relays, setRelays] = useState<Relay[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [orderDateFilter, setOrderDateFilter] = useState('all');
  const [orderSortBy, setOrderSortBy] = useState('created_at');
  const [orderSortOrder, setOrderSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState<'cancel' | 'archive' | 'mark_paid' | 'delete' | null>(null);
  const [deleteConfirmationCode, setDeleteConfirmationCode] = useState('');
  const [userConfirmationCode, setUserConfirmationCode] = useState('');
  const [siteSettings, setSiteSettings] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [footerSections, setFooterSections] = useState<any[]>([]);
  const [cmsContent, setCmsContent] = useState<any[]>([]);
  const [visitorDetails, setVisitorDetails] = useState<VisitorDetail[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = usePersistentTabState(
    'admin-dashboard:activeTab',
    'dashboard',
    ['dashboard', 'personnel', 'relays', 'orders', 'cms', 'api-keys', 'disputes', 'marketing', 'chat-requests', 'monitoring']
  );
  const [configTab, setConfigTab] = usePersistentTabState(
    'admin-dashboard:configTab',
    'cms',
    ['cms', 'banners', 'stock', 'config']
  );
  const [personnelTab, setPersonnelTab] = usePersistentTabState(
    'admin-dashboard:personnelTab',
    'users',
    ['users', 'vendors', 'employees']
  );
  const [hrTab, setHrTab] = usePersistentTabState(
    'admin-dashboard:hrTab',
    'employees',
    ['employees', 'leaves', 'evaluations', 'trainings', 'documents']
  );
  const [marketingTab, setMarketingTab] = usePersistentTabState(
    'admin-dashboard:marketingTab',
    'newsletter',
    ['newsletter', 'advertisements', 'promo-codes', 'deals']
  );
  const [chatTab, setChatTab] = usePersistentTabState(
    'admin-dashboard:chatTab',
    'messaging',
    ['messaging', 'chat-access']
  );

  // Dialog states
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [relayDialog, setRelayDialog] = useState(false);

  // Form states
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [editingRelay, setEditingRelay] = useState<Relay | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderActionsDialog, setOrderActionsDialog] = useState(false);

  const [employeeForm, setEmployeeForm] = useState({
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    department: 'Opérations',
    hire_date: '',
    salary_amount: '',
    salary_currency: 'USD',
    status: 'active',
    birth_date: '',
    address: '',
    manager_id: '',
    relay_id: '',
    create_user_account: false,
    user_password: ''
  });

  const [relayForm, setRelayForm] = useState({
    name: '',
    address: '',
    contact_name: '',
    contact_phone: '',
    latitude: '',
    longitude: '',
    active: true
  });

  useEffect(() => {
    if (user) {
      fetchAllData();
    }

    // Subscribe to real-time order updates
    const channel = supabase
      .channel('admin-orders-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders'
        },
        () => {
          if (user) {
            fetchOrders();
            fetchAnalytics(); // Also refresh analytics since order status affects stats
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAnalytics(),
        fetchUsers(),
        fetchVendorApplications(),
        fetchEmployees(),
        fetchRelays(),
        fetchOrders(),
        fetchSiteSettings(),
        fetchCategories(),            
        fetchFooterSections(),
        fetchCmsContent(),
        fetchVisitorDetails()
      ]);
    } catch (error) {
      console.error(t('admin.error.loading_data'), error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const [
        usersRes, 
        ordersRes, 
        relaysRes, 
        employeesRes,
        processedOrdersRes,
        pendingOrdersRes,
        disputesRes,
        visitorsRes,
        satisfiedCustomersRes
      ] = await Promise.all([
        supabase.from('profiles').select('id, system_role, vendor_status'),
        supabase.from('orders').select('id, amount_cents, currency, product:products(price_cents)').in('status', ['PAID', 'AWAITING_DROP_OFF', 'AVAILABLE_FOR_PICKUP', 'PICKED_UP']),
        supabase.from('relays').select('id'),
        supabase.from('employees').select('id'),
        supabase.from('orders').select('id').in('status', ['PICKED_UP', 'AVAILABLE_FOR_PICKUP']),
        supabase.from('orders').select('id').in('status', ['PAID', 'AWAITING_DROP_OFF']),
        supabase.from('order_disputes').select('id').eq('status', 'pending'),
        supabase.from('visitor_analytics').select('visitor_id').gte('created_at', thirtyDaysAgo.toISOString()),
        supabase.from('product_reviews').select('user_id').gte('rating', 3)
      ]);

      const users = usersRes.data || [];
      const orders = ordersRes.data || [];

      // Revenue: use amount_cents if set, fallback to product.price_cents
      const revenueCurrency = (orders.find((o: any) => o.currency) as any)?.currency || 'USD';
      const totalRevenue = orders.reduce((sum, order: any) => {
        const direct = order.amount_cents || 0;
        if (direct > 0) return sum + direct;
        return sum + ((order.product as any)?.price_cents || 0);
      }, 0);
      
      // Count unique visitors from last 30 days
      const uniqueVisitors = new Set(visitorsRes.data?.map(v => v.visitor_id) || []).size;

      setAnalytics({
        totalUsers: users.length,
        totalVendors: users.filter(u => (u as any).vendor_status === 'approved').length,
        totalOrders: orders.length,
        totalRevenue: totalRevenue / 100,
        revenueCurrency,
        totalRelays: relaysRes.data?.length || 0,
        totalEmployees: employeesRes.data?.length || 0,
        activeUsers: users.filter(u => u.system_role !== 'admin' && u.system_role !== 'super_admin').length,
        processedOrders: processedOrdersRes.data?.length || 0,
        pendingOrders: pendingOrdersRes.data?.length || 0,
        disputes: disputesRes.data?.length || 0,
        visitors: uniqueVisitors,
        satisfiedCustomers: new Set((satisfiedCustomersRes.data || []).map((r: any) => r.user_id)).size,
      });
    } catch (error) {
      console.error(t('admin.error.analytics'), error);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAllUsers((data || []) as any);
    } catch (error) {
      console.error('Erreur utilisateurs:', error);
    }
  };

  const fetchVendorApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('vendor_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVendorApplications(data || []);
    } catch (error) {
      console.error('Erreur demandes vendeurs:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Erreur employés:', error);
    }
  };

  const fetchRelays = async () => {
    try {
      const { data, error } = await supabase
        .from('relays')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRelays(data || []);
    } catch (error) {
      console.error('Erreur relays:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      // Récupérer les commandes avec order_items pour les commandes sans product_id direct
      const { data: ordersData, error } = await supabase
        .from('orders')
        .select(`
          *,
          product:products(title),
          relay:relays(name),
          pickup_tokens(qr_svg, otp_expires_at),
          order_items(
            product_id,
            quantity,
            unit_price,
            products(title)
          )
        `)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      
      if (ordersData && ordersData.length > 0) {
        // Récupérer tous les IDs uniques de clients et vendeurs
        const clientIds = [...new Set(ordersData.map(o => o.client_id).filter(Boolean))];
        const vendorIds = [...new Set(ordersData.map(o => o.vendor_id).filter(Boolean))];
        const allProfileIds = [...new Set([...clientIds, ...vendorIds])];
        
        // Récupérer les profils en une seule requête
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', allProfileIds);
        
        // Créer un map pour lookup rapide
        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        
        // Enrichir les commandes avec les données client/vendor et product depuis order_items
        const enrichedOrders = ordersData.map(order => {
          // Get product from direct relation or from order_items
          let productData = order.product;
          if (!productData && order.order_items && order.order_items.length > 0) {
            const firstItem = order.order_items[0] as any;
            productData = firstItem.products;
          }
          
          return {
            ...order,
            product: productData,
            client: profilesMap.get(order.client_id) || null,
            vendor: profilesMap.get(order.vendor_id) || null
          };
        });
        
        setOrders(enrichedOrders as any);
        setFilteredOrders(enrichedOrders as any);
      } else {
        setOrders([]);
        setFilteredOrders([]);
      }
    } catch (error) {
      console.error('Erreur commandes:', error);
    }
  };

  // Filter and sort orders based on status, search, date and sort options
  useEffect(() => {
    let filtered = [...orders];

    // Filter by status
    if (orderStatusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === orderStatusFilter);
    }

    // Filter by search query
    if (orderSearchQuery.trim()) {
      const query = orderSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(order => 
        order.order_number?.toLowerCase().includes(query) ||
        order.id.toLowerCase().includes(query) ||
        order.client?.full_name?.toLowerCase().includes(query) ||
        order.vendor?.full_name?.toLowerCase().includes(query) ||
        order.product?.title?.toLowerCase().includes(query) ||
        order.relay?.name?.toLowerCase().includes(query)
      );
    }

    // Filter by date
    if (orderDateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        
        switch (orderDateFilter) {
          case 'today':
            return orderDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return orderDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return orderDate >= monthAgo;
          case 'year':
            const yearAgo = new Date(today);
            yearAgo.setFullYear(yearAgo.getFullYear() - 1);
            return orderDate >= yearAgo;
          default:
            return true;
        }
      });
    }

    // Sort orders
    filtered.sort((a, b) => {
      let compareResult = 0;
      
      switch (orderSortBy) {
        case 'created_at':
          compareResult = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'amount_cents':
          compareResult = a.amount_cents - b.amount_cents;
          break;
        case 'order_number':
          compareResult = (a.order_number || '').localeCompare(b.order_number || '');
          break;
        case 'status':
          compareResult = a.status.localeCompare(b.status);
          break;
        case 'client':
          compareResult = (a.client?.full_name || '').localeCompare(b.client?.full_name || '');
          break;
        case 'vendor':
          compareResult = (a.vendor?.full_name || '').localeCompare(b.vendor?.full_name || '');
          break;
        default:
          compareResult = 0;
      }
      
      return orderSortOrder === 'asc' ? compareResult : -compareResult;
    });

    setFilteredOrders(filtered);
  }, [orders, orderStatusFilter, orderSearchQuery, orderDateFilter, orderSortBy, orderSortOrder]);

  const handleClearOrderFilters = () => {
    setOrderStatusFilter('all');
    setOrderSearchQuery('');
    setOrderDateFilter('all');
    setOrderSortBy('created_at');
    setOrderSortOrder('desc');
  };

  const handleToggleSortOrder = () => {
    setOrderSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const fetchSiteSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      setSiteSettings(data || []);
    } catch (error) {
      console.error('Erreur paramètres:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      
      // Fetch products to calculate accurate counts using category_id
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('category_id')
        .eq('active', true);

      if (productsError) throw productsError;

      // Calculate accurate product counts for each category
      const categoriesWithCounts = (data || []).map((category) => {
        const matchingProducts = productsData?.filter((product) => 
          product.category_id === category.id
        );
        return {
          ...category,
          product_count: matchingProducts?.length || 0
        };
      });
      
      setCategories(categoriesWithCounts);
    } catch (error) {
      console.error('Erreur catégories:', error);
    }
  };

  const fetchFooterSections = async () => {
    try {
      const { data, error } = await supabase
        .from('footer_sections')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setFooterSections(data || []);
    } catch (error) {
      console.error('Erreur footer sections:', error);
    }
  };

  const fetchCmsContent = async () => {
    try {
      const { data, error } = await supabase
        .from('cms_content')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setCmsContent(data || []);
    } catch (error) {
      console.error('Erreur CMS content:', error);
    }
  };

  const fetchVisitorDetails = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Get the latest visit for each unique visitor
      const { data, error } = await supabase
        .from('visitor_analytics')
        .select('visitor_id, ip_address, country, city, page_url, device_type, browser, created_at')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Group by visitor_id and keep only the latest visit
      const uniqueVisitors = new Map<string, VisitorDetail>();
      data?.forEach(visit => {
        if (!uniqueVisitors.has(visit.visitor_id)) {
          uniqueVisitors.set(visit.visitor_id, visit as VisitorDetail);
        }
      });

      setVisitorDetails(Array.from(uniqueVisitors.values()));
    } catch (error) {
      console.error('Erreur détails visiteurs:', error);
    }
  };

  const handleEmployeeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let createdUserId = null;
      
      // Create user account if requested
      if (!editingEmployee && employeeForm.create_user_account) {
        if (!employeeForm.email || !employeeForm.user_password) {
          toast({
            title: "Erreur",
            description: "Email et mot de passe requis pour créer un compte utilisateur",
            variant: "destructive"
          });
          return;
        }

        // Validation du mot de passe
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{10,}$/;
        if (!passwordRegex.test(employeeForm.user_password)) {
          toast({
            title: "Erreur",
            description: "Le mot de passe doit contenir au minimum 10 caractères avec : majuscule, minuscule, chiffre et caractère spécial",
            variant: "destructive"
          });
          return;
        }

        // Create user account with Supabase Auth
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: employeeForm.email,
          password: employeeForm.user_password,
          options: {
            data: {
              full_name: `${employeeForm.first_name} ${employeeForm.last_name}`,
              phone: employeeForm.phone,
              role: 'client',
              user_role: 'employee'
            }
          }
        });

        if (authError) throw authError;
        
        if (authData.user) {
          createdUserId = authData.user.id;
          
          // Update profile with employee-specific role
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              full_name: `${employeeForm.first_name} ${employeeForm.last_name}`,
              phone: employeeForm.phone,
              role: 'client',
              user_role: 'employee'
            })
            .eq('id', authData.user.id);

          if (profileError) throw profileError;
        }
      }

      let employeeData = {
        ...employeeForm,
        salary_amount: employeeForm.salary_amount ? parseFloat(employeeForm.salary_amount) : null,
        hire_date: employeeForm.hire_date || new Date().toISOString().split('T')[0],
        birth_date: employeeForm.birth_date || null,
        manager_id: employeeForm.manager_id || null,
        relay_id: employeeForm.relay_id || null,
        user_id: createdUserId // Link employee to user account if created
      };

      // Remove form-specific fields that don't belong in the database
      delete employeeData.create_user_account;
      delete employeeData.user_password;

      if (editingEmployee) {
        const { error } = await supabase
          .from('employees')
          .update(employeeData)
          .eq('id', editingEmployee.id);

        if (error) throw error;
        toast({ title: "Employé modifié avec succès" });
      } else {
        // For new employees, generate employee code if not provided
        if (!employeeData.employee_code || employeeData.employee_code.trim() === '') {
          const { data: generatedCode, error: codeError } = await supabase
            .rpc('generate_employee_code');
          
          if (codeError) throw codeError;
          employeeData.employee_code = generatedCode;
        }

        const { error } = await supabase
          .from('employees')
          .insert(employeeData);

        if (error) throw error;
        
        let successMessage = "Employé créé avec succès";
        if (employeeForm.create_user_account && createdUserId) {
          successMessage += " avec compte utilisateur";
        }
        toast({ title: successMessage });
      }

      await fetchEmployees();
      await fetchAnalytics();
      setEmployeeDialog(false);
      resetEmployeeForm();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleRelaySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const relayData = {
        ...relayForm,
        latitude: relayForm.latitude ? parseFloat(relayForm.latitude) : null,
        longitude: relayForm.longitude ? parseFloat(relayForm.longitude) : null
      };

      if (editingRelay) {
        const { error } = await supabase
          .from('relays')
          .update(relayData)
          .eq('id', editingRelay.id);

        if (error) throw error;
        toast({ title: "Point relais modifié avec succès" });
      } else {
        const { error } = await supabase
          .from('relays')
          .insert(relayData);

        if (error) throw error;
        toast({ title: "Point relais créé avec succès" });
      }

      await fetchRelays();
      await fetchAnalytics();
      setRelayDialog(false);
      resetRelayForm();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleEditEmployee = (employee: Employee) => {
    setEmployeeForm({
      employee_code: employee.employee_code,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email || '',
      phone: employee.phone || '',
      position: employee.position,
      department: employee.department || 'Opérations',
      hire_date: employee.hire_date,
      salary_amount: employee.salary_amount?.toString() || '',
      salary_currency: employee.salary_currency || 'USD',
      status: employee.status,
      birth_date: employee.birth_date || '',
      address: employee.address || '',
      manager_id: employee.manager_id || '',
      relay_id: employee.relay_id || '',
      create_user_account: false,
      user_password: ''
    });
    setEditingEmployee(employee);
    setEmployeeDialog(true);
  };

  const handleEditRelay = (relay: Relay) => {
    setRelayForm({
      name: relay.name,
      address: relay.address,
      contact_name: relay.contact_name,
      contact_phone: relay.contact_phone,
      latitude: relay.latitude?.toString() || '',
      longitude: relay.longitude?.toString() || '',
      active: relay.active
    });
    setEditingRelay(relay);
    setRelayDialog(true);
  };

  const handleDeleteEmployee = async (employeeId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) return;
    
    try {
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', employeeId);
      
      if (error) throw error;
      toast({ title: "Employé supprimé avec succès" });
      await fetchEmployees();
      await fetchAnalytics();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteRelay = async (relayId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce point relais ?')) return;
    
    try {
      const { error } = await supabase
        .from('relays')
        .delete()
        .eq('id', relayId);
      
      if (error) throw error;
      toast({ title: "Point relais supprimé avec succès" });
      await fetchRelays();
      await fetchAnalytics();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      employee_code: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      department: 'Opérations',
      hire_date: '',
      salary_amount: '',
      salary_currency: 'USD',
      status: 'active',
      birth_date: '',
      address: '',
      manager_id: '',
      relay_id: '',
      create_user_account: false,
      user_password: ''
    });
    setEditingEmployee(null);
  };

  const resetRelayForm = () => {
    setRelayForm({
      name: '',
      address: '',
      contact_name: '',
      contact_phone: '',
      latitude: '',
      longitude: '',
      active: true
    });
    setEditingRelay(null);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING_PAYMENT':
        return <Badge variant="outline" className="text-orange-600 border-orange-300">En attente de paiement</Badge>;
      case 'PAID':
        return <Badge variant="outline" className="text-blue-600 border-blue-300">Payé</Badge>;
      case 'AVAILABLE_FOR_PICKUP':
        return <Badge variant="outline" className="text-green-600 border-green-300">Disponible</Badge>;
      case 'PICKED_UP':
        return <Badge variant="outline" className="text-purple-600 border-purple-300">Récupéré</Badge>;
      case 'EXPIRED':
        return <Badge variant="outline" className="text-red-600 border-red-300">Expiré</Badge>;
      case 'CANCELLED':
        return <Badge variant="outline" className="text-red-600 border-red-300">Annulé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatPrice = (cents: number) => {
    // Affiche les montants en CDF sans décimales (usage courant en RDC)
    const amount = Math.round(cents / 100);
    return new Intl.NumberFormat('fr-CD', {
      maximumFractionDigits: 0
    }).format(amount) + ' CDF';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">{t('admin.loading')}</p>
        </div>
      </div>
    );
  }

  const revenueCents = Math.round(analytics.totalRevenue * 100);
  const convertedRevenue = convertPrice(revenueCents, analytics.revenueCurrency || 'USD', displayCurrency);
  // Format amount without currency symbol (shown in badge)
  const formattedRevenueAmount = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(convertedRevenue / 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      
      <div className="container mx-auto px-4 py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              {t('admin.title')}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t('admin.subtitle')}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <GlobalSearch onNavigate={(tab) => setActiveTab(tab)} />
            <ClearCacheButton />
            <TestEmailButton />
            <Badge variant="outline" className="bg-white/80 backdrop-blur-sm px-4 py-2 shadow-lg">
              <Activity className="h-4 w-4 mr-2" />
              {t('admin.system_operational')}
            </Badge>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="flex flex-wrap gap-1 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-2">
            <TabsTrigger value="dashboard" className="flex items-center space-x-2 rounded-lg">
              <BarChart3 className="h-4 w-4" />
              <span>{t('admin.tabs.dashboard')}</span>
            </TabsTrigger>
            <TabsTrigger value="personnel" className="flex items-center space-x-2 rounded-lg">
              <Users className="h-4 w-4" />
              <span>Personnel</span>
            </TabsTrigger>
            <TabsTrigger value="relays" className="flex items-center space-x-2 rounded-lg">
              <MapPin className="h-4 w-4" />
              <span>{t('admin.tabs.relays')}</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center space-x-2 rounded-lg">
              <ShoppingCart className="h-4 w-4" />
              <span>{t('admin.tabs.orders')}</span>
            </TabsTrigger>
            {isAdminOrSuperAdmin && (
              <TabsTrigger value="cms" className="flex items-center space-x-2 rounded-lg">
                <Settings className="h-4 w-4" />
                <span>Configuration</span>
              </TabsTrigger>
            )}
            {isAdminOrSuperAdmin && (
              <TabsTrigger value="api-keys" className="flex items-center space-x-2 rounded-lg">
                <Key className="h-4 w-4" />
                <span>{t('admin.api_keys.tab')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="disputes" className="flex items-center space-x-2 rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <span>Réclamations</span>
            </TabsTrigger>
            <TabsTrigger value="marketing" className="flex items-center space-x-2 rounded-lg">
              <Zap className="h-4 w-4" />
              <span>Marketing</span>
            </TabsTrigger>
            <TabsTrigger value="chat-requests" className="flex items-center space-x-2 rounded-lg">
              <MessageSquare className="h-4 w-4" />
              <span>Chat</span>
            </TabsTrigger>
            {isAdminOrSuperAdmin && (
              <TabsTrigger value="monitoring" className="flex items-center space-x-2 rounded-lg">
                <Activity className="h-4 w-4" />
                <span>Monitoring</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <Accordion type="multiple" defaultValue={["main-stats", "detailed-stats"]} className="space-y-4">
              {/* Statistiques principales */}
              <AccordionItem value="main-stats" className="border rounded-lg bg-white/50 backdrop-blur-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    <span className="text-lg font-semibold">Statistiques principales</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                    <Card 
                      className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => { setPersonnelTab('users'); setActiveTab('personnel'); }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                            <Users className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-blue-700">{t('admin.analytics.users')}</p>
                            <p className="text-2xl font-bold text-blue-900">{analytics.totalUsers}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => { setPersonnelTab('vendors'); setActiveTab('personnel'); }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-green-500 rounded-xl shadow-lg">
                            <Store className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-green-700">{t('admin.analytics.vendors')}</p>
                            <p className="text-2xl font-bold text-green-900">{analytics.totalVendors}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => setActiveTab('orders')}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                            <ShoppingCart className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-purple-700">{t('admin.analytics.orders')}</p>
                            <p className="text-2xl font-bold text-purple-900">{analytics.totalOrders}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-200/50 shadow-lg hover:shadow-xl transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-orange-500 rounded-xl shadow-lg flex-shrink-0">
                            <span className="text-white text-lg font-bold">{getCurrencySymbol(displayCurrency)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-orange-700">{t('admin.analytics.revenue')}</p>
                            <p className="text-base font-bold text-orange-900 break-all leading-tight">{formattedRevenueAmount}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="bg-gradient-to-br from-red-50 to-red-100/50 border-red-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => setActiveTab('relays')}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-red-500 rounded-xl shadow-lg">
                            <MapPin className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-red-700">{t('admin.analytics.relays')}</p>
                            <p className="text-2xl font-bold text-red-900">{analytics.totalRelays}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 border-indigo-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => { setPersonnelTab('employees'); setActiveTab('personnel'); }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-indigo-500 rounded-xl shadow-lg">
                            <Briefcase className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-indigo-700">{t('admin.analytics.employees')}</p>
                            <p className="text-2xl font-bold text-indigo-900">{analytics.totalEmployees}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 border-yellow-200/50 shadow-lg hover:shadow-xl transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-yellow-500 rounded-xl shadow-lg">
                            <Star className="h-6 w-6 text-white fill-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-yellow-700">Clients satisfaits</p>
                            <p className="text-2xl font-bold text-yellow-900">{analytics.satisfiedCustomers}</p>
                            <p className="text-xs text-yellow-600">avis ≥ 3★</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Statistiques détaillées */}
              <AccordionItem value="detailed-stats" className="border rounded-lg bg-white/50 backdrop-blur-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-2">
                    <Activity className="h-5 w-5 text-primary" />
                    <span className="text-lg font-semibold">Statistiques détaillées</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    <Card 
                      className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 border-cyan-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => { setPersonnelTab('users'); setActiveTab('personnel'); }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-cyan-500 rounded-xl shadow-lg">
                            <UserCheckIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-cyan-700">Utilisateurs actifs</p>
                            <p className="text-2xl font-bold text-cyan-900">{analytics.activeUsers}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => setActiveTab('orders')}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-emerald-500 rounded-xl shadow-lg">
                            <CheckCircleIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-emerald-700">Commandes traitées</p>
                            <p className="text-2xl font-bold text-emerald-900">{analytics.processedOrders}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => setActiveTab('orders')}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-amber-500 rounded-xl shadow-lg">
                            <ClockIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-amber-700">Commandes en cours</p>
                            <p className="text-2xl font-bold text-amber-900">{analytics.pendingOrders}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card 
                      className="bg-gradient-to-br from-rose-50 to-rose-100/50 border-rose-200/50 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                      onClick={() => setActiveTab('disputes')}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-rose-500 rounded-xl shadow-lg">
                            <MessageSquare className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-rose-700">Réclamations en attente</p>
                            <p className="text-2xl font-bold text-rose-900">{analytics.disputes}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-violet-50 to-violet-100/50 border-violet-200/50 shadow-lg hover:shadow-xl transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-violet-500 rounded-xl shadow-lg">
                            <Radio className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-violet-700">Visiteurs (30j)</p>
                            <p className="text-2xl font-bold text-violet-900">{analytics.visitors}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Détails des visiteurs */}
              <AccordionItem value="visitor-details" className="border rounded-lg bg-white/50 backdrop-blur-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-2">
                    <Radio className="h-5 w-5 text-primary" />
                    <span className="text-lg font-semibold">Visiteurs (30j) - {visitorDetails.length} visiteurs uniques</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="rounded-lg border bg-white/80 backdrop-blur-sm overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Adresse IP</TableHead>
                          <TableHead>Pays</TableHead>
                          <TableHead>Ville</TableHead>
                          <TableHead>Dernière page</TableHead>
                          <TableHead>Device</TableHead>
                          <TableHead>Navigateur</TableHead>
                          <TableHead>Dernière visite</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visitorDetails.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                              Aucun visiteur enregistré pour les 30 derniers jours
                            </TableCell>
                          </TableRow>
                        ) : (
                          visitorDetails.map((visitor) => (
                            <TableRow key={visitor.visitor_id}>
                              <TableCell className="font-mono text-sm">
                                {visitor.ip_address || '-'}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-mono">
                                  {visitor.country || '-'}
                                </Badge>
                              </TableCell>
                              <TableCell>{visitor.city || '-'}</TableCell>
                              <TableCell className="max-w-xs truncate" title={visitor.page_url}>
                                {visitor.page_url}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">
                                  {visitor.device_type || '-'}
                                </Badge>
                              </TableCell>
                              <TableCell>{visitor.browser || '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {new Date(visitor.created_at).toLocaleString('fr-FR')}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>

          {/* Personnel Tab */}
          <TabsContent value="personnel" className="space-y-6">
            <div className="mb-6">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                Gestion du Personnel
              </h2>
              <p className="text-muted-foreground mt-2">
                Gérez les utilisateurs, vendeurs et employés de la plateforme
              </p>
            </div>

            <Tabs value={personnelTab} onValueChange={setPersonnelTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-3 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
                <TabsTrigger value="users" className="flex items-center space-x-2 rounded-lg">
                  <Users className="h-4 w-4" />
                  <span>{t('admin.tabs.users')}</span>
                </TabsTrigger>
                <TabsTrigger value="vendors" className="flex items-center space-x-2 rounded-lg">
                  <Store className="h-4 w-4" />
                  <span>{t('admin.tabs.vendors')}</span>
                </TabsTrigger>
                <TabsTrigger value="employees" className="flex items-center space-x-2 rounded-lg">
                  <UserCheck className="h-4 w-4" />
                  <span>{t('admin.tabs.employees')}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="users" className="space-y-6">
                <UsersManager onRefresh={fetchAllData} user={user} />
              </TabsContent>

              <TabsContent value="vendors" className="space-y-6">
                <VendorsManager onRefresh={fetchAllData} user={profile} />
              </TabsContent>

              <TabsContent value="employees" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200/50 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                          <Briefcase className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-700">{t('hr.total_employees')}</p>
                          <p className="text-2xl font-bold text-blue-900">{employees.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200/50 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-green-500 rounded-xl shadow-lg">
                          <UserCheck className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-700">{t('hr.active')}</p>
                          <p className="text-2xl font-bold text-green-900">
                            {employees.filter(e => e.status === 'active').length}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-200/50 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-orange-500 rounded-xl shadow-lg">
                          <Building2 className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-orange-700">{t('hr.positions')}</p>
                          <p className="text-2xl font-bold text-orange-900">
                            {new Set(employees.map(e => e.position)).size}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200/50 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                          <Building2 className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-purple-700">{t('hr.departments')}</p>
                          <p className="text-2xl font-bold text-purple-900">
                            {new Set(employees.map(e => e.department)).size}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Tabs value={hrTab} onValueChange={setHrTab} className="space-y-6">
                  <TabsList className="grid w-full grid-cols-5 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
                    <TabsTrigger value="employees">{t('hr.employees')}</TabsTrigger>
                    <TabsTrigger value="leaves">{t('hr.leave')}</TabsTrigger>
                    <TabsTrigger value="evaluations">{t('hr.evaluations')}</TabsTrigger>
                    <TabsTrigger value="trainings">{t('hr.training')}</TabsTrigger>
                    <TabsTrigger value="documents">{t('hr.documents')}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="employees" className="space-y-6">
                    <EmployeesManager />
                  </TabsContent>

                  <TabsContent value="leaves" className="space-y-6">
                    <LeaveManagement />
                  </TabsContent>

                  <TabsContent value="evaluations" className="space-y-6">
                    <EvaluationManagement />
                  </TabsContent>

                  <TabsContent value="trainings" className="space-y-6">
                    <TrainingManagement />
                  </TabsContent>

                  <TabsContent value="documents" className="space-y-6">
                    <DocumentsManagement />
                  </TabsContent>
                </Tabs>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Relays Tab */}
          <TabsContent value="relays" className="space-y-6">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <MapPin className="h-6 w-6 text-primary" />
                      <span>{t('relay.points')}</span>
                    </CardTitle>
                    <CardDescription>
                      Gérez le réseau de points de retrait
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      onClick={() => { resetRelayForm(); setRelayDialog(true); }}
                      className="bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('relay.new_point')}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/relay-points?highlight=' + (relays[0]?.id || ''))}
                      className="bg-white/50 backdrop-blur-sm"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {t('relay.view_map')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('relay.point')}</TableHead>
                      <TableHead>{t('relay.contact')}</TableHead>
                      <TableHead>{t('relay.address')}</TableHead>
                      <TableHead>{t('relay.status')}</TableHead>
                      <TableHead>{t('relay.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relays.map((relay) => (
                      <TableRow key={relay.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                              <MapPin className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{relay.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {relay.latitude && relay.longitude && 
                                  `${relay.latitude.toFixed(4)}, ${relay.longitude.toFixed(4)}`
                                }
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{relay.contact_name}</p>
                            <p className="text-sm text-muted-foreground">{relay.contact_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>{relay.address}</TableCell>
                        <TableCell>
                          <Badge variant={relay.active ? 'outline' : 'secondary'}>
                            {relay.active ? t('active') : t('inactive')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditRelay(relay)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              {t('edit')}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteRelay(relay.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <ShoppingCart className="h-6 w-6 text-primary" />
                      <span>{t('orders.recent')}</span>
                    </CardTitle>
                    <CardDescription>
                      {filteredOrders.length} commande(s) {orderStatusFilter !== 'all' || orderSearchQuery || orderDateFilter !== 'all' ? 'filtrée(s)' : 'au total'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <AdminOrderFilters
                  statusFilter={orderStatusFilter}
                  searchQuery={orderSearchQuery}
                  dateFilter={orderDateFilter}
                  sortBy={orderSortBy}
                  sortOrder={orderSortOrder}
                  onStatusChange={setOrderStatusFilter}
                  onSearchChange={setOrderSearchQuery}
                  onDateChange={setOrderDateFilter}
                  onSortByChange={setOrderSortBy}
                  onSortOrderToggle={handleToggleSortOrder}
                  onClearFilters={handleClearOrderFilters}
                />

                {/* Bulk Actions Bar */}
                {selectedOrderIds.length > 0 && (
                  <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="font-medium">
                        {selectedOrderIds.length} commande(s) sélectionnée(s)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBulkAction('cancel');
                          setBulkActionDialog(true);
                        }}
                        disabled={selectedOrderIds.length === 0}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Annuler
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBulkAction('archive');
                          setBulkActionDialog(true);
                        }}
                        disabled={selectedOrderIds.length === 0}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Archiver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBulkAction('mark_paid');
                          setBulkActionDialog(true);
                        }}
                        disabled={selectedOrderIds.length === 0}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Marquer payées
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          setBulkAction('delete');
                          setUserConfirmationCode('');
                          setBulkActionDialog(true);
                          
                          try {
                            // Générer le code et récupérer les infos en une seule requête RPC
                            const { data: rpcData, error: rpcError } = await (supabase
                              .rpc as any)('request_deletion_confirmation_code', { order_ids: selectedOrderIds });
                            
                            if (rpcError) {
                              console.error('Failed to generate code:', rpcError);
                              toast({
                                title: "Erreur",
                                description: "Impossible de générer le code de confirmation.",
                                variant: "destructive",
                              });
                              setBulkActionDialog(false);
                              return;
                            }

                            // La fonction RPC retourne déjà le code, mais on doit envoyer l'email manuellement
                            // car pg_net ne fonctionne pas. Utiliser les données retournées par RPC
                            
                            console.log('[DELETION] RPC Data:', rpcData);
                            console.log('[DELETION] RPC Data type:', typeof rpcData);
                            console.log('[DELETION] RPC Data.code:', (rpcData as any)?.code);
                            console.log('[DELETION] email_sent value:', (rpcData as any)?.email_sent);
                            
                            // Toujours envoyer via Edge Function car pg_net ne fonctionne pas
                            try {
                              // L'email n'a pas été envoyé par la fonction SQL, on l'envoie depuis le frontend
                              const { data: { user } } = await supabase.auth.getUser();
                              
                              console.log('[DELETION] Getting admin emails...');
                              // Créer une fonction RPC pour obtenir les emails (ne nécessite pas auth.admin)
                              const { data: adminEmails } = await (supabase
                                .rpc as any)('get_super_admin_emails');
                              
                              console.log('[DELETION] Admin emails:', adminEmails);

                              // Envoyer l'email via Edge Function
                              console.log('[DELETION] Calling Edge Function...');
                              const response = await fetch(
                                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-deletion-code`,
                                {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                                  },
                                  body: JSON.stringify({
                                    code: (typeof rpcData === 'object' ? (rpcData as any)?.code : rpcData) || 'CODE_ERROR',
                                    requested_by_name: user?.user_metadata?.full_name || 'Admin',
                                    requested_by_email: user?.email || '',
                                    order_count: selectedOrderIds.length,
                                    super_admins: adminEmails || [],
                                    expires_at: (rpcData as any).expires_at,
                                  }),
                                }
                              );

                              console.log('[DELETION] Edge Function response status:', response.status);

                              if (!response.ok) {
                                const errorText = await response.text();
                                console.error('[DELETION] Edge Function error:', errorText);
                                toast({
                                  title: "Avertissement",
                                  description: "Code généré mais email non envoyé. Vérifiez les logs.",
                                  variant: "destructive",
                                });
                              } else {
                                const result = await response.json();
                                console.log('[DELETION] Email sent successfully:', result);
                                toast({
                                  title: "Code envoyé",
                                  description: (rpcData as any).message || "Code de confirmation envoyé par email.",
                                });
                              }
                            } catch (emailError) {
                              console.error('[DELETION] Failed to send email:', emailError);
                              toast({
                                title: "Code généré",
                                description: "Code créé mais l'envoi d'email a échoué. Vérifiez la console.",
                                variant: "destructive",
                              });
                            }
                          } catch (error) {
                            console.error('Failed to send deletion code:', error);
                            toast({
                              title: "Erreur",
                              description: "Impossible d'envoyer le code de confirmation.",
                              variant: "destructive",
                            });
                            setBulkActionDialog(false);
                          }
                        }}
                        disabled={selectedOrderIds.length === 0}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedOrderIds([])}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Désélectionner
                      </Button>
                    </div>
                  </div>
                )}
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.length === filteredOrders.length && filteredOrders.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedOrderIds(filteredOrders.map(o => o.id));
                            } else {
                              setSelectedOrderIds([]);
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                        />
                      </TableHead>
                      <TableHead>{t('orders.order')}</TableHead>
                      <TableHead>{t('orders.client')}</TableHead>
                      <TableHead>{t('orders.vendor')}</TableHead>
                      <TableHead>{t('relay.point')}</TableHead>
                      <TableHead>{t('orders.amount')}</TableHead>
                      <TableHead>{t('status')}</TableHead>
                      <TableHead>{t('date')}</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          Aucune commande trouvée
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOrders.map((order) => (
                      <TableRow
                        key={order.id}
                        className={`hover:bg-muted/50 ${
                          selectedOrderIds.includes(order.id) ? 'bg-primary/5' : ''
                        }`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedOrderIds([...selectedOrderIds, order.id]);
                              } else {
                                setSelectedOrderIds(selectedOrderIds.filter(id => id !== order.id));
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                          />
                        </TableCell>
                        <TableCell
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderActionsDialog(true);
                          }}
                        >
                          <div>
                            <p className="font-medium">{order.product?.title || t('orders.unknown_product')}</p>
                            <p className="text-sm text-muted-foreground">#{order.order_number || order.id.slice(0, 8)}</p>
                          </div>
                        </TableCell>
                        <TableCell
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderActionsDialog(true);
                          }}
                        >{order.client?.full_name || t('orders.unknown_client')}</TableCell>
                        <TableCell
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderActionsDialog(true);
                          }}
                        >{order.vendor?.full_name || t('orders.unknown_vendor')}</TableCell>
                        <TableCell
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderActionsDialog(true);
                          }}
                        >{order.relay?.name || t('orders.unknown_relay')}</TableCell>
                        <TableCell
                          className="font-mono cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderActionsDialog(true);
                          }}
                        >{formatPriceWithCurrency(order.amount_cents, order.currency || 'CDF', displayCurrency, convertPrice)}</TableCell>
                        <TableCell
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderActionsDialog(true);
                          }}
                        >{getStatusBadge(order.status)}</TableCell>
                        <TableCell
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderActionsDialog(true);
                          }}
                        >{new Date(order.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrder(order);
                              setOrderActionsDialog(true);
                            }}
                          >
                            Actions
                          </Button>
                        </TableCell>
                      </TableRow>
                    )))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Configuration Tab (admin/super_admin only) */}
          {isAdminOrSuperAdmin && <TabsContent value="cms" className="space-y-6">
            <div className="mb-6">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                {t('cms.site_configuration_title')}
              </h2>
              <p className="text-muted-foreground mt-2">
                {t('cms.site_configuration_subtitle')}
              </p>
            </div>

            <Tabs value={configTab} onValueChange={setConfigTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
                <TabsTrigger value="cms" className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>{t('cms.tabs.site_content')}</span>
                </TabsTrigger>
                <TabsTrigger value="banners" className="flex items-center space-x-2">
                  <Zap className="h-4 w-4" />
                  <span>Bannières</span>
                </TabsTrigger>
                <TabsTrigger value="stock" className="flex items-center space-x-2">
                  <Package className="h-4 w-4" />
                  <span>Mode Test Stock</span>
                </TabsTrigger>
                <TabsTrigger value="config" className="flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>{t('cms.tabs.system_settings')}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="cms" className="space-y-6">
                <CMSManager 
                  siteSettings={siteSettings}
                  categories={categories}
                  footerSections={footerSections}
                  cmsContent={cmsContent}
                  onRefresh={fetchAllData}
                  user={user}
                />
              </TabsContent>

              <TabsContent value="banners" className="space-y-6">
                <SystemBannersManager />
              </TabsContent>

              <TabsContent value="stock" className="space-y-6">
                <StockTestModeManager />
              </TabsContent>

              <TabsContent value="config" className="space-y-6">
                <ConfigurationManager />
              </TabsContent>
            </Tabs>
          </TabsContent>}

          {/* API Keys Tab (admin/super_admin only) */}
          {isAdminOrSuperAdmin && <TabsContent value="api-keys" className="space-y-6">
            <div className="mb-6">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent flex items-center gap-2">
                <Key className="h-8 w-8" />
                {t('admin.api_keys.title')}
              </h2>
              <p className="text-muted-foreground mt-2">
                {t('admin.api_keys.description')}
              </p>
            </div>
            <APIKeysManager />
          </TabsContent>}

          {/* Disputes Tab */}
          <TabsContent value="disputes" className="space-y-6">
            <AdminDisputesManager />
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing" className="space-y-6">
            <div className="mb-6">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                Marketing & Promotions
              </h2>
              <p className="text-muted-foreground mt-2">
                Gérez vos campagnes marketing, publicités et offres promotionnelles
              </p>
            </div>

            <Tabs value={marketingTab} onValueChange={setMarketingTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
                <TabsTrigger value="newsletter" className="flex items-center space-x-2 rounded-lg">
                  <Mail className="h-4 w-4" />
                  <span>Newsletter</span>
                </TabsTrigger>
                <TabsTrigger value="advertisements" className="flex items-center space-x-2 rounded-lg">
                  <Radio className="h-4 w-4" />
                  <span>Publicités</span>
                </TabsTrigger>
                <TabsTrigger value="promo-codes" className="flex items-center space-x-2 rounded-lg">
                  <Tag className="h-4 w-4" />
                  <span>Codes Promo</span>
                </TabsTrigger>
                <TabsTrigger value="deals" className="flex items-center space-x-2 rounded-lg">
                  <Percent className="h-4 w-4" />
                  <span>Deals</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="newsletter" className="space-y-6">
                <NewsletterManager />
              </TabsContent>

              <TabsContent value="advertisements" className="space-y-6">
                <AdvertisementsManager />
              </TabsContent>

              <TabsContent value="promo-codes" className="space-y-6">
                <PromoCodesManager />
              </TabsContent>

              <TabsContent value="deals" className="space-y-6">
                <EnhancedDealsManagerV2 />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Chat Requests Tab */}
          <TabsContent value="chat-requests" className="space-y-6">
            <div className="mb-6">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                Chat & Messagerie
              </h2>
              <p className="text-muted-foreground mt-2">
                Communiquez avec vos vendeurs et gérez les demandes de chat
              </p>
            </div>
            <Tabs value={chatTab} onValueChange={setChatTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
                <TabsTrigger value="messaging" className="flex items-center space-x-2 rounded-lg">
                  <MessageSquare className="h-4 w-4" />
                  <span>Messagerie vendeurs</span>
                </TabsTrigger>
                <TabsTrigger value="chat-access" className="flex items-center space-x-2 rounded-lg">
                  <MessageSquare className="h-4 w-4" />
                  <span>Demandes de chat</span>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="messaging">
                <AdminMessagingCenter />
              </TabsContent>
              <TabsContent value="chat-access">
                <ChatRequestsManager />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Monitoring Tab (admin/super_admin only) */}
          {isAdminOrSuperAdmin && (
            <TabsContent value="monitoring" className="space-y-6">
              <MonitoringPanel />
            </TabsContent>
          )}

        </Tabs>

        {/* Order Actions Dialog */}
        <OrderActionsDialog
          order={selectedOrder}
          isOpen={orderActionsDialog}
          onClose={() => {
            setOrderActionsDialog(false);
            setSelectedOrder(null);
          }}
          onRefresh={fetchAllData}
        />

        {/* Employee Management Dialog */}
        <Dialog open={employeeDialog} onOpenChange={setEmployeeDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{editingEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}</DialogTitle>
              <DialogDescription>
                {editingEmployee ? 'Modifiez les informations de l\'employé' : 'Ajoutez un nouvel employé au système'}
              </DialogDescription>
            </DialogHeader>
            
            <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
              <form onSubmit={handleEmployeeSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="employee_code">
                      Code employé 
                      {!editingEmployee && <span className="text-sm text-muted-foreground ml-1">(généré automatiquement si vide)</span>}
                    </Label>
                    <Input
                      id="employee_code"
                      value={employeeForm.employee_code}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, employee_code: e.target.value }))}
                      placeholder={editingEmployee ? "EMP001" : "Laissez vide pour génération automatique"}
                      required={!!editingEmployee}
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Statut</Label>
                    <Select value={employeeForm.status} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, status: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Actif</SelectItem>
                        <SelectItem value="inactive">Inactif</SelectItem>
                        <SelectItem value="terminated">Terminé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Prénom</Label>
                    <Input
                      id="first_name"
                      value={employeeForm.first_name}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nom</Label>
                    <Input
                      id="last_name"
                      value={employeeForm.last_name}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={employeeForm.email}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      value={employeeForm.phone}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>

                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <Label htmlFor="position">Position</Label>
                     <Select value={employeeForm.position} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, position: value }))}>
                       <SelectTrigger>
                         <SelectValue placeholder="Sélectionnez une position" />
                       </SelectTrigger>
                       <SelectContent>
                         <SelectItem value="Directeur Général">Directeur Général</SelectItem>
                         <SelectItem value="Directeur des Opérations">Directeur des Opérations</SelectItem>
                         <SelectItem value="Manager">Manager</SelectItem>
                         <SelectItem value="Superviseur">Superviseur</SelectItem>
                         <SelectItem value="Chef d'équipe">Chef d'équipe</SelectItem>
                         <SelectItem value="Opérateur de Point Relais">Opérateur de Point Relais</SelectItem>
                         <SelectItem value="Agent Commercial">Agent Commercial</SelectItem>
                         <SelectItem value="Responsable RH">Responsable RH</SelectItem>
                         <SelectItem value="Assistant RH">Assistant RH</SelectItem>
                         <SelectItem value="Comptable">Comptable</SelectItem>
                         <SelectItem value="Assistant Comptable">Assistant Comptable</SelectItem>
                         <SelectItem value="Développeur">Développeur</SelectItem>
                         <SelectItem value="Technicien IT">Technicien IT</SelectItem>
                         <SelectItem value="Agent de Sécurité">Agent de Sécurité</SelectItem>
                         <SelectItem value="Agent de Maintenance">Agent de Maintenance</SelectItem>
                         <SelectItem value="Réceptionniste">Réceptionniste</SelectItem>
                         <SelectItem value="Assistant Administratif">Assistant Administratif</SelectItem>
                         <SelectItem value="Stagiaire">Stagiaire</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>
                  <div>
                    <Label htmlFor="department">Département</Label>
                    <Select value={employeeForm.department} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, department: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Opérations">Opérations</SelectItem>
                        <SelectItem value="RH">Ressources Humaines</SelectItem>
                        <SelectItem value="IT">Informatique</SelectItem>
                        <SelectItem value="Finance">Finance</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hire_date">Date d'embauche</Label>
                    <Input
                      id="hire_date"
                      type="date"
                      value={employeeForm.hire_date}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, hire_date: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="birth_date">Date de naissance</Label>
                    <Input
                      id="birth_date"
                      type="date"
                      value={employeeForm.birth_date}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, birth_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="salary_amount">Salaire</Label>
                    <Input
                      id="salary_amount"
                      type="number"
                      value={employeeForm.salary_amount}
                      onChange={(e) => setEmployeeForm(prev => ({ ...prev, salary_amount: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="salary_currency">Devise</Label>
                    <Select value={employeeForm.salary_currency} onValueChange={(value) => setEmployeeForm(prev => ({ ...prev, salary_currency: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="CDF">CDF</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Adresse</Label>
                  <Textarea
                    id="address"
                    value={employeeForm.address}
                    onChange={(e) => setEmployeeForm(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Adresse complète"
                  />
                </div>

                {/* Section création de compte utilisateur - uniquement pour nouveaux employés */}
                {!editingEmployee && (
                  <>
                    <Separator />
                    
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="create_user_account"
                          checked={employeeForm.create_user_account}
                          onCheckedChange={(checked) => setEmployeeForm(prev => ({ ...prev, create_user_account: checked }))}
                        />
                        <Label htmlFor="create_user_account" className="font-medium">
                          Créer un compte utilisateur pour cet employé
                        </Label>
                      </div>
                      
                      {employeeForm.create_user_account && (
                        <div>
                          <Label htmlFor="user_password">Mot de passe du compte</Label>
                           <Input
                             id="user_password"
                             type="password"
                             value={employeeForm.user_password}
                             onChange={(e) => setEmployeeForm(prev => ({ ...prev, user_password: e.target.value }))}
                             placeholder="Minimum 10 caractères avec majuscule, minuscule, chiffre et caractère spécial"
                             minLength={10}
                             required={employeeForm.create_user_account}
                           />
                           <p className="text-sm text-muted-foreground mt-1">
                             Doit contenir : au moins 10 caractères, 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial (@$!%*?&)
                           </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEmployeeDialog(false)}>
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingEmployee ? 'Modifier' : 'Créer'}
                  </Button>
                </DialogFooter>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Relay Management Dialog */}
        <Dialog open={relayDialog} onOpenChange={setRelayDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingRelay ? 'Modifier le point relais' : 'Nouveau point relais'}</DialogTitle>
              <DialogDescription>
                {editingRelay ? 'Modifiez les informations du point relais' : 'Ajoutez un nouveau point de retrait'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleRelaySubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nom du point relais</Label>
                <Input
                  id="name"
                  value={relayForm.name}
                  onChange={(e) => setRelayForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="address">Adresse</Label>
                <Textarea
                  id="address"
                  value={relayForm.address}
                  onChange={(e) => setRelayForm(prev => ({ ...prev, address: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name">Nom du contact</Label>
                  <Input
                    id="contact_name"
                    value={relayForm.contact_name}
                    onChange={(e) => setRelayForm(prev => ({ ...prev, contact_name: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Téléphone</Label>
                  <Input
                    id="contact_phone"
                    value={relayForm.contact_phone}
                    onChange={(e) => setRelayForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={relayForm.latitude}
                    onChange={(e) => setRelayForm(prev => ({ ...prev, latitude: e.target.value }))}
                    placeholder="-4.3217"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={relayForm.longitude}
                    onChange={(e) => setRelayForm(prev => ({ ...prev, longitude: e.target.value }))}
                    placeholder="15.3122"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={relayForm.active}
                  onCheckedChange={(checked) => setRelayForm(prev => ({ ...prev, active: checked }))}
                />
                <Label htmlFor="active">Point relais actif</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRelayDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit">
                  {editingRelay ? 'Modifier' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Actions Dialog */}
        <Dialog open={bulkActionDialog} onOpenChange={setBulkActionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {bulkAction === 'cancel' && 'Annuler les commandes'}
                {bulkAction === 'archive' && 'Archiver les commandes'}
                {bulkAction === 'mark_paid' && 'Marquer comme payées'}
                {bulkAction === 'delete' && 'Supprimer les commandes'}
              </DialogTitle>
              <DialogDescription>
                Vous êtes sur le point d'effectuer cette action sur {selectedOrderIds.length} commande(s).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                {bulkAction === 'cancel' && 'Les commandes seront annulées et les clients en seront notifiés.'}
                {bulkAction === 'archive' && 'Les commandes seront archivées et retirées de la liste principale.'}
                {bulkAction === 'mark_paid' && 'Les commandes seront marquées comme payées.'}
                {bulkAction === 'delete' && 'Les commandes seront définitivement supprimées de la base de données. Cette action est irréversible !'}
              </p>
              
              {bulkAction === 'delete' && (
                <div className="mt-4 space-y-3">
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-sm font-medium text-destructive">
                      Un code de confirmation a été envoyé par email aux super administrateurs.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Le code est valide pendant 15 minutes. Vérifiez vos emails.
                    </p>
                  </div>
                  <Input
                    placeholder="Saisissez le code reçu par email"
                    value={userConfirmationCode}
                    onChange={(e) => setUserConfirmationCode(e.target.value.toUpperCase())}
                    className="text-center text-lg font-mono"
                    autoFocus
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkActionDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    if (bulkAction === 'cancel') {
                      const { error } = await supabase
                        .from('orders')
                        .update({ status: 'CANCELLED', updated_at: new Date().toISOString() })
                        .in('id', selectedOrderIds);
                      
                      if (error) throw error;
                      
                      // Log the action
                      await (supabase.rpc as any)('log_bulk_order_action', {
                        order_ids: selectedOrderIds,
                        action_type: 'cancel',
                        reason: 'Bulk cancellation by admin'
                      });
                      
                      toast({
                        title: "Commandes annulées",
                        description: `${selectedOrderIds.length} commande(s) ont été annulées.`,
                      });
                    } else if (bulkAction === 'archive') {
                      const { error } = await supabase
                        .from('orders')
                        .update({ archived: true, updated_at: new Date().toISOString() } as any)
                        .in('id', selectedOrderIds);
                      
                      if (error) throw error;
                      
                      // Log the action
                      await (supabase.rpc as any)('log_bulk_order_action', {
                        order_ids: selectedOrderIds,
                        action_type: 'archive',
                        reason: 'Bulk archiving by admin'
                      });
                      
                      toast({
                        title: "Commandes archivées",
                        description: `${selectedOrderIds.length} commande(s) ont été archivées.`,
                      });
                    } else if (bulkAction === 'mark_paid') {
                      const { error } = await supabase
                        .from('orders')
                        .update({ status: 'PAID', updated_at: new Date().toISOString() })
                        .in('id', selectedOrderIds);
                      
                      if (error) throw error;
                      
                      // Log the action
                      await (supabase.rpc as any)('log_bulk_order_action', {
                        order_ids: selectedOrderIds,
                        action_type: 'mark_paid',
                        reason: 'Bulk payment marking by admin'
                      });
                      
                      toast({
                        title: "Commandes mises à jour",
                        description: `${selectedOrderIds.length} commande(s) marquées comme payées.`,
                      });
                    } else if (bulkAction === 'delete') {
                      // Verify confirmation code
                      if (!userConfirmationCode || userConfirmationCode.length < 6) {
                        toast({
                          title: "Code requis",
                          description: "Veuillez saisir le code de confirmation reçu par email.",
                          variant: "destructive",
                        });
                        return;
                      }
                      
                      console.log('[DELETE] Calling admin_delete_orders with code:', userConfirmationCode);
                      console.log('[DELETE] Order IDs:', selectedOrderIds);
                      
                      // Use the RPC function to safely delete orders with related data
                      const { data, error } = await (supabase
                        .rpc as any)('admin_delete_orders', { 
                          order_ids: selectedOrderIds,
                          confirmation_code: userConfirmationCode
                        });
                      
                      console.log('[DELETE] Response data:', data);
                      console.log('[DELETE] Response error:', error);
                      
                      if (error) throw error;
                      
                      // Function returns { deleted_count, failed_count, ... } — no "success" field
                      if (!data || (data as any).deleted_count === undefined) {
                        toast({
                          title: "Erreur de suppression",
                          description: (data as any)?.message || 'Failed to delete orders',
                          variant: "destructive",
                        });
                        return;
                      }
                      
                      // Check if there were any failures
                      if ((data as any)?.failed_count > 0) {
                        const firstReason = (data as any).failed_orders?.[0]?.reason || 'erreur inconnue';
                        console.error('[DELETE] Failed orders:', (data as any).failed_orders);
                        toast({
                          title: "Suppression partielle",
                          description: `${(data as any).deleted_count ?? 0} supprimées, ${(data as any).failed_count} échouées. Raison: ${firstReason}`,
                          variant: "destructive",
                        });
                      } else {
                        toast({
                          title: "Commandes supprimées",
                          description: `${(data as any)?.deleted_count || 0} commande(s) ont été définitivement supprimées.`,
                        });
                      }

                      // Send deletion report email
                      try {
                        console.log('[DELETE] Sending deletion report...');
                        const session = (await supabase.auth.getSession()).data.session;
                        const reportResponse = await fetch(
                          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-deletion-report`,
                          {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${session?.access_token}`,
                            },
                            body: JSON.stringify({
                              admin_email: session?.user?.email,
                              admin_name: session?.user?.user_metadata?.full_name || session?.user?.email,
                              deleted_count: (data as any).deleted_count,
                              total_requested: selectedOrderIds.length,
                              deleted_orders: (data as any).deleted_orders,
                              failed_count: (data as any).failed_count || 0,
                            }),
                          }
                        );

                        const reportResult = await reportResponse.json();
                        console.log('[DELETE] Report email result:', reportResult);

                        if (reportResult.success) {
                          toast({
                            title: "Rapport envoyé",
                            description: "Un rapport détaillé a été envoyé par email.",
                          });
                        }
                      } catch (reportError) {
                        console.error('[DELETE] Failed to send report:', reportError);
                        // Don't block the UI if report fails
                      }
                    }
                    
                    setSelectedOrderIds([]);
                    setUserConfirmationCode('');
                    setDeleteConfirmationCode('');
                    setBulkActionDialog(false);
                    fetchOrders();
                  } catch (error) {
                    console.error('Erreur action en masse:', error);
                    toast({
                      title: "Erreur",
                      description: "Une erreur est survenue lors de l'action en masse.",
                      variant: "destructive",
                    });
                  }
                }}
                variant={bulkAction === 'cancel' || bulkAction === 'delete' ? 'destructive' : 'default'}
                disabled={bulkAction === 'delete' && (!userConfirmationCode || userConfirmationCode.length < 6)}
              >
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;